import React, { FC, useContext, useState } from "react"
import styled from "styled-components"
import xss from "xss"

import { generateConditionalClassName } from "@utils"

import { Icon, ICON_TYPES } from "@helpscout/hs-marketing-shared-components"
import { MEDIA_QUERIES } from "@constants"
import ContentfulLpContext from "@context/ContentfulLpContext/ContentfulLpContext"

const PricingTableFeatureLI = styled.li`
  border-bottom: 1px solid var(--color--charcoal-200);
  margin: 0;
  padding: var(--spacing--base-1-5) 0;
  transition: max-height 0.3s;

  &:not(.has-no-description) {
    cursor: pointer;

    &:hover {
      background: var(--color--charcoal-100);
      border: 1px solid var(--color--charcoal-200);
      border-radius: var(--border-radius--M);
      padding: var(--spacing--base-1-5) calc(var(--spacing--base-1) - 1px);
      margin: -1px calc(var(--spacing--base-1) * -1) 0;
    }
  }

  .feature--description {
    flex-wrap: nowrap;
    gap: 0;
    justify-content: flex-start;
    margin-bottom: var(--spacing--S);

    > div:first-child {
      align-items: center;
      display: flex;
      height: calc(var(--font-size--L) * var(--line-height--relaxed));
    }

    .Icon {
      margin-left: -8px;
      transition: transform 0.3s;
    }

    h5 {
      margin: 0;
      text-wrap: balance;
      color: var(--color--text-default);
      transition: color 0.3s;
    }
    
    p {
      max-height: 0;
      opacity: 0;
      margin: 0;
      overflow: hidden;
      text-wrap: balance;
      transition: all 0.3s;
    }
  }

  &.has-no-description {
    .feature--description .Icon {
      opacity: 0;
    }
  }

  .feature--plans {
    gap: var(--spacing--base-0-75);
  }

  .feature--in-plan {
    text-align: center;
    flex: 0 0 calc(33.3333% - var(--spacing--base-0-75) * 2 / 3);
    margin: auto;
    text-wrap: balance;

    .icon-wrapper {
      align-items: center;
      background: var(--color--cobalt-600);
      border-radius: 50%;
      display: flex;
      height: 24px;
      justify-content: center;
      margin: 0 auto;
      width: 24px;
    }
  }

  &.is-expanded {
    .feature--description {
      h5 {
        color: var(--color--text-dark);
      }

      .Icon {
        transform: rotate(90deg);
      }

      p {
        max-height: calc(var(--font-size--S) * var(--line-height--relaxed) * 4);
        opacity: 1;
        margin-top: calc(var(--spacing--base-0-5)/2);
      }
    }
  }

  ${MEDIA_QUERIES.L} {
    .feature--description {
      margin: 0;
    }
  }
`

const PricingTableFeature: FC<{
  feature: any,
}> = ({
  feature,
}) => {
    const { isAnnualDiscountApplied, isSalesPage } = useContext(ContentfulLpContext)
    const [isExpanded, setIsExpanded] = useState<boolean>(false)
    const hasNoDescription = !feature.description

    const planKeys = isSalesPage ?
      ["planStandard", "planPlus", "planPro"] :
      ["planFree", "planStandard", "planPlus"]

    const planColumns =
      (isAnnualDiscountApplied && feature.isAnnualDiscountApplied) ?
        [
          feature.isAnnualDiscountApplied[planKeys[0]],
          feature.isAnnualDiscountApplied[planKeys[1]],
          feature.isAnnualDiscountApplied[planKeys[2]]
        ] :
        [
          feature[planKeys[0]],
          feature[planKeys[1]],
          feature[planKeys[2]]
        ]

    return (
      <PricingTableFeatureLI
        className={generateConditionalClassName({
          "feature": true,
          "has-no-description": hasNoDescription,
          "is-expanded": isExpanded,
        })}
        onClick={() => !hasNoDescription && setIsExpanded(_isExpanded => !_isExpanded)}
      >
        <div className="grid">
          <div className="feature--description column column--1-4-L column--1-5-XL flex">
            <div>
              <Icon
                color="var(--color--text-default)"
                size="26px"
                type={ICON_TYPES["chevron-right"]}
              />
            </div>
            <div>
              <h5
                className="type--text-medium-L"
                dangerouslySetInnerHTML={{ __html: feature.title }}
              />
              <p
                className="type--text-S"
                dangerouslySetInnerHTML={{ __html: feature.description }}
              />
            </div>
          </div>
          <div className="feature--plans flex column column--4-13-L column--5-13-XL">
            {planColumns.map((featureInPlan, i) => (
              <div className="feature--in-plan" key={`${feature.title}--${i}`}>
                {featureInPlan === "x" && (
                  <div className="icon-wrapper">
                    <Icon
                      color="var(--color--basics-white)"
                      size="20px"
                      type={ICON_TYPES["checkmark"]}
                    />
                  </div>
                )}
                {featureInPlan && featureInPlan !== "x" && (
                  <span className="type--text-S color--cobalt-700" dangerouslySetInnerHTML={{ __html: xss(featureInPlan) }} />
                )}
                {!featureInPlan && (
                  <span className="not-included type--text-S color--text-disabled">–</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </PricingTableFeatureLI>
    )
  }

export default PricingTableFeature
